import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, bulkDeleteGroups } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Group } from '../../../../types';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import Typography from '@mui/material/Typography';

interface DeleteGroupsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  selectedGroup: Group;
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
}

export default function DeleteGroupsDialog({
  open,
  handleClose,
  selectedRows,
  selectedGroup,
  setSelectedRows,
}: DeleteGroupsDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const { changeLogRoles, currentTier, features } = useUserSettings();
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: (logMessage: string) =>
      bulkDeleteGroups(selectedRows, 'Group', logMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity:
          currentTier === 'enterprise' &&
          isFeatureEnabled('indexGroupsReact', features)
            ? 'success'
            : 'info',
        message:
          currentTier === 'enterprise' &&
          isFeatureEnabled('indexGroupsReact', features)
            ? Messages.Success.Deleted('Group')
            : 'The task to delete the group(s) has begun.',
      });
      setSelectedRows([]);
      if (currentTier === 'enterprise') {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.GroupsIndexedPage],
        });
      }
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting the selected group(s). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.groups.delete_groups, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const handleConfirm = () => {
    if (
      isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.DeleteGroup.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate('');
    }
  };

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title={selectedRows.length > 1 ? 'Delete Groups' : 'Delete Group'}
        dialogContent={
          <Typography variant="body1">
            {selectedRows.length > 1 ? (
              <>
                Delete <strong>{selectedRows.length}</strong> groups?
              </>
            ) : (
              <>
                Delete <strong>{selectedGroup?.email}</strong>?
              </>
            )}
          </Typography>
        }
        primaryButton={{
          title: selectedRows.length > 1 ? 'Delete Groups' : 'Delete Group',
          color: 'error',
          action: handleConfirm,
          loading: isPending,
          disabled: isPending,
        }}
      />
      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            mutate(changeLogMessage);
          }}
          mutateLoading={isPending}
        />
      )}
    </>
  );
}
